<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Extremum } from '@quicktide/shared'
import type { Point } from '../lib/tide'
import { useAppStore } from '../store'

const props = defineProps<{
  /** Astronomical prediction */
  points: Point[]
  /** Measured levels, if any */
  observed?: Point[]
  /** Forecast including weather, if any */
  forecast?: Point[]
  /** The astronomy with the skipper's own rise or fall added, standing in for the two above */
  surged?: Point[]
  extrema: Extremum[]
  /** Seabed over a shallow, in cm NAP; shades everything below it */
  seabed?: number | null
  /** The level from which the shallow is passable, in cm NAP */
  passable?: number | null
  /** Underside of a fixed bridge, in cm NAP; shades everything above it */
  ceiling?: number | null
  /** The level below which the bridge can be passed, in cm NAP */
  limit?: number | null
  /**
   * Draw the boat at the current level instead of the plain "now" line, with the mast or the
   * keel measured against what it has to clear. `measureTo` is the bridge underside or the
   * seabed, in cm NAP; `fits` colours the dimension.
   */
  vessel?: { mastHeight?: number; draught?: number } | null
  vesselLevel?: number | null
  measureTo?: number | null
  fits?: boolean
  /** Where the boat sits along the day; defaults to `now`. Tapping or dragging moves it. */
  markerTime?: number | null
  /**
   * Stretches of the day to band across the top of the plot, as the old app does. A band
   * may carry its own words -- the times a shallow can be crossed -- else `bandLabel` names it.
   */
  bands?: { state: string; from: number; to: number; label?: string }[]
  /** Turns a band's state into the word shown on it */
  bandLabel?: (state: string) => string
  dayStart: number
  dayEnd: number
  now: number
  /**
   * A level to hold against the curve, in cm at the chart's own datum, or null for none.
   * While it is up, a finger anywhere on the chart moves it up and down, and the moment line
   * steps aside: the reader asks "when is the water this high", the times come out under the
   * chart, and a knob the size of a fingertip at the edge was too small to catch on a phone.
   */
  levelCursor?: number | null
}>()

const emit = defineEmits<{
  'update:markerTime': [time: number]
  'update:levelCursor': [level: number]
  /**
   * A pinch or a wheel, as one step wider (1) or one narrower (-1), about which moment.
   * A step rather than a ratio: the spans are a fixed ladder, and a gesture that reports
   * the hundredth of a percent it moved since the last event never climbs a rung.
   */
  zoom: [direction: 1 | -1, about: number]
  /** A two-finger drag: how far to move the visible window, in milliseconds */
  pan: [by: number]
}>()

const store = useAppStore()
const svgEl = ref<SVGSVGElement | null>(null)
const W = 360
const H = 200
const pad = { top: 18, right: 12, bottom: 26, left: 44 }
const cw = W - pad.left - pad.right
const ch = H - pad.top - pad.bottom

/** The time under a pointer, clamped to the day on screen */
function timeAt(clientX: number): number {
  const r = svgEl.value!.getBoundingClientRect()
  const userX = ((clientX - r.left) / r.width) * W
  const fraction = Math.max(0, Math.min(1, (userX - pad.left) / cw))
  return props.dayStart + fraction * (props.dayEnd - props.dayStart)
}

let dragging = false
/**
 * Two fingers work the time axis: the distance between them zooms, their middle pans.
 * One finger goes on moving the boat, which is what it has always done here, so neither
 * gesture has to be learned at the cost of the other.
 */
const touches = new Map<number, number>()
let pinch: { span: number; middle: number } | null = null
/** How far apart the fingers must travel, in proportion, to be worth a rung */
const RUNG = 1.3

const settingLevel = () => props.levelCursor !== null && props.levelCursor !== undefined

function onDown(e: PointerEvent) {
  dragging = true
  try {
    ;(e.currentTarget as Element).setPointerCapture(e.pointerId)
  } catch {
    // carry on without capture
  }
  if (settingLevel()) emitLevel(e.clientY)
  else emit('update:markerTime', timeAt(e.clientX))
}

function onPointerDown(e: PointerEvent) {
  touches.set(e.pointerId, e.clientX)
  if (touches.size === 2) {
    const [a, b] = [...touches.values()]
    pinch = { span: Math.abs(a! - b!), middle: (a! + b!) / 2 }
    return
  }
  onDown(e)
}

function onPointerMove(e: PointerEvent) {
  if (!touches.has(e.pointerId)) return onMove(e)
  touches.set(e.pointerId, e.clientX)
  if (touches.size < 2 || !pinch) return onMove(e)
  const [a, b] = [...touches.values()]
  const span = Math.abs(a! - b!)
  const middle = (a! + b!) / 2
  const box = svgEl.value!.getBoundingClientRect()
  emit('pan', -((middle - pinch.middle) / box.width) * (props.dayEnd - props.dayStart))
  if (pinch.span > 8 && span > 8) {
    const ratio = span / pinch.span
    if (ratio > RUNG) { emit('zoom', -1, timeAt(middle)); pinch = { span, middle }; return }
    if (ratio < 1 / RUNG) { emit('zoom', 1, timeAt(middle)); pinch = { span, middle }; return }
    // the fingers have not moved far enough yet: keep the span they started from
    pinch = { span: pinch.span, middle }
    return
  }
  pinch = { span, middle }
}

function onPointerUp(e: PointerEvent) {
  touches.delete(e.pointerId)
  if (touches.size < 2) pinch = null
  onUp()
}

/** The level under a pointer, in centimetres at the datum the data is in */
function levelAtY(clientY: number): number | null {
  const plot = view.value
  if (!plot || !svgEl.value) return null
  const box = svgEl.value.getBoundingClientRect()
  const userY = ((clientY - box.top) / box.height) * H
  const fraction = Math.max(0, Math.min(1, (userY - pad.top) / ch))
  const shown = plot.max - fraction * (plot.max - plot.min)
  // out of the reader's chosen datum and back into the one the levels are stored in
  return Math.round(shown * 100 - store.displayLevel(0) * 100)
}

function emitLevel(clientY: number) {
  const level = levelAtY(clientY)
  if (level !== null) emit('update:levelCursor', level)
}

function onWheel(e: WheelEvent) {
  if (!svgEl.value) return
  e.preventDefault()
  emit('zoom', e.deltaY > 0 ? 1 : -1, timeAt(e.clientX))
}

function onMove(e: PointerEvent) {
  if (!dragging) return
  e.preventDefault()
  if (settingLevel()) emitLevel(e.clientY)
  else emit('update:markerTime', timeAt(e.clientX))
}
function onUp() {
  dragging = false
}

const view = computed(() => {
  if (props.points.length === 0) return null
  const all = [...props.points, ...(props.observed ?? []), ...(props.forecast ?? []), ...(props.surged ?? [])]
  const levels = all.map(p => store.displayLevel(p.level))
  // the seabed and the passable level have to be on the scale, or the shading makes no sense
  if (props.seabed !== null && props.seabed !== undefined) levels.push(store.displayLevel(props.seabed))
  if (props.passable !== null && props.passable !== undefined) levels.push(store.displayLevel(props.passable))
  if (props.ceiling !== null && props.ceiling !== undefined) levels.push(store.displayLevel(props.ceiling))
  if (props.limit !== null && props.limit !== undefined) levels.push(store.displayLevel(props.limit))
  if (props.vessel && props.vesselLevel !== null && props.vesselLevel !== undefined) {
    // the masthead and the keel are the point of the picture; leave them room
    if (props.vessel.mastHeight) levels.push(store.displayLevel(props.vesselLevel + props.vessel.mastHeight))
    if (props.vessel.draught) levels.push(store.displayLevel(props.vesselLevel - props.vessel.draught))
  }
  let min = Math.min(...levels)
  let max = Math.max(...levels)
  // round the scale to whole decimetres with a little headroom
  min = Math.floor(min * 10 - 1) / 10
  max = Math.ceil(max * 10 + 1) / 10
  const span = props.dayEnd - props.dayStart
  const x = (t: number) => pad.left + ((t - props.dayStart) / span) * cw
  const y = (m: number) => pad.top + ch - ((m - min) / (max - min)) * ch
  const toPath = (pts: Point[]) => {
    // a gap in the data breaks the line instead of bridging it
    let d = ''
    let prev: Point | undefined
    for (const p of pts) {
      const move = !prev || p.time - prev.time > 20 * 60_000
      d += `${move ? 'M' : 'L'}${x(p.time).toFixed(1)} ${y(store.displayLevel(p.level)).toFixed(1)} `
      prev = p
    }
    return d.trim()
  }
  const path = toPath(props.points)
  const area = `${path} L${x(props.points[props.points.length - 1]!.time).toFixed(1)} ${(pad.top + ch).toFixed(1)} L${x(props.points[0]!.time).toFixed(1)} ${(pad.top + ch).toFixed(1)} Z`
  const observedPath = props.observed?.length ? toPath(props.observed) : ''
  const forecastPath = props.forecast?.length ? toPath(props.forecast) : ''
  const surgedPath = props.surged?.length ? toPath(props.surged) : ''
  const yTicks: { m: number; y: number }[] = []
  const stepM = (max - min) > 3 ? 1 : 0.5
  for (let m = Math.ceil(min / stepM) * stepM; m <= max + 1e-9; m += stepM) yTicks.push({ m, y: y(m) })
  /*
   * Ticks that suit the span on show: every three hours across a day, closer together when
   * the chart is zoomed into a few hours, and dates once more than a couple of days are in
   * view. Day ticks land on local midnight rather than on multiples of the epoch.
   */
  const hours = span / 3_600_000
  const xTicks: { x: number; label: string }[] = []
  if (hours > 54) {
    const days = Math.ceil(hours / 24)
    const stepDays = Math.max(1, Math.ceil(days / 8))
    const first = new Date(props.dayStart)
    first.setHours(0, 0, 0, 0)
    for (let d = new Date(first); d.getTime() <= props.dayEnd; d.setDate(d.getDate() + stepDays)) {
      if (d.getTime() < props.dayStart) continue
      xTicks.push({ x: x(d.getTime()), label: d.toLocaleDateString(store.settings.locale, { day: 'numeric', month: 'short' }) })
    }
  } else {
    const stepHours = hours <= 4 ? 0.5 : hours <= 9 ? 1 : hours <= 18 ? 2 : hours <= 30 ? 3 : 6
    const step = stepHours * 3_600_000
    const midnight = new Date(props.dayStart)
    midnight.setHours(0, 0, 0, 0)
    let t = midnight.getTime()
    while (t < props.dayStart) t += step
    for (; t <= props.dayEnd; t += step) {
      const at = new Date(t)
      xTicks.push({ x: x(t), label: `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}` })
    }
  }

  // beyond a couple of days the day's own high and low waters crowd into one another,
  // and the shape of the fortnight is what the chart is for at that width
  const labelled = span <= 2 * 86_400_000
  const cursorY = props.levelCursor === null || props.levelCursor === undefined
    ? null
    : y(store.displayLevel(props.levelCursor))

  const marks = props.extrema.map(e => {
    const mx = x(e.time)
    const my = y(store.displayLevel(e.level))
    // a high water at the top of the scale would put its figure under the bands; beside the dot instead
    const tucked = e.type === 'HW' && my - 8 < pad.top + 9
    return {
      x: mx, y: my, type: e.type,
      label: labelled ? store.displayLevel(e.level).toFixed(2) : '',
      lx: tucked ? mx + 7 : mx,
      ly: e.type === 'HW' ? (tucked ? my + 3.5 : my - 8) : my + 14,
      anchor: tucked ? 'start' : 'middle'
    }
  })
  // bands run across the top of the plot, clipped to the day, so they line up with the curve
  /*
   * A state's word goes inside its band, where it fits. A band's own words -- the times a
   * shallow can be crossed -- go above it instead, because a two-hour window is narrower than
   * its own times; they may run past the ends of the band, but not into the next one's.
   */
  let clear = -Infinity
  const bands = (props.bands ?? [])
    .map(b => ({ ...b, from: Math.max(b.from, props.dayStart), to: Math.min(b.to, props.dayEnd) }))
    .filter(b => b.to > b.from)
    .map(b => {
      const x1 = x(b.from)
      const x2 = x(b.to)
      if (b.label) {
        const half = (b.label.length * 4.2) / 2
        const mid = Math.max(pad.left + half, Math.min(W - pad.right - half, (x1 + x2) / 2))
        const fits = mid - half > clear + 4
        if (fits) clear = mid + half
        return { state: b.state, x1, x2, text: '', above: fits ? b.label : '', mid }
      }
      const text = props.bandLabel ? props.bandLabel(b.state) : b.state
      // words only where they fit, at roughly four units a character in the band's type
      return { state: b.state, x1, x2, text: x2 - x1 > Math.max(34, text.length * 4 + 6) ? text : '', above: '', mid: (x1 + x2) / 2 }
    })

  const nowX = props.now >= props.dayStart && props.now <= props.dayEnd ? x(props.now) : null
  const markAt = props.markerTime ?? props.now
  const markX = markAt >= props.dayStart && markAt <= props.dayEnd && cursorY === null ? x(markAt) : null

  /**
   * The boat: hull on the waterline, mast above it or keel below, and a dimension line to
   * whatever it has to clear. Only drawn when "now" is on screen and there is a level to
   * float it at.
   */
  let vessel = null as null | {
    x: number; deckY: number; mastY: number | null; keelY: number | null
    dim: { x: number; from: number; to: number; label: string } | null
  }
  if (props.vessel && markX !== null && props.vesselLevel !== null && props.vesselLevel !== undefined) {
    const deckY = y(store.displayLevel(props.vesselLevel))
    const mastY = props.vessel.mastHeight ? y(store.displayLevel(props.vesselLevel + props.vessel.mastHeight)) : null
    const keelY = props.vessel.draught ? y(store.displayLevel(props.vesselLevel - props.vessel.draught)) : null
    let dim = null as null | { x: number; from: number; to: number; label: string }
    if (props.measureTo !== null && props.measureTo !== undefined) {
      const targetY = y(store.displayLevel(props.measureTo))
      const tipY = mastY ?? keelY
      if (tipY !== null) {
        const metres = Math.abs(props.measureTo - props.vesselLevel - (props.vessel.mastHeight ?? -(props.vessel.draught ?? 0))) / 100
        dim = { x: Math.min(markX + 22, W - pad.right - 6), from: tipY, to: targetY, label: `${metres.toFixed(2)} m` }
      }
    }
    vessel = { x: markX, deckY, mastY, keelY, dim }
  }
  const seabedY = props.seabed === null || props.seabed === undefined ? null : y(store.displayLevel(props.seabed))
  const seabedHeight = seabedY === null ? 0 : Math.max(0, pad.top + ch - seabedY)
  const passableY = props.passable === null || props.passable === undefined ? null : y(store.displayLevel(props.passable))
  const ceilingY = props.ceiling === null || props.ceiling === undefined ? null : y(store.displayLevel(props.ceiling))
  const ceilingHeight = ceilingY === null ? 0 : Math.max(0, ceilingY - pad.top)
  const limitY = props.limit === null || props.limit === undefined ? null : y(store.displayLevel(props.limit))
  // the days either side of a trial device's window: marked, so an empty stretch of chart
  // reads as a limit and not as data that failed to arrive
  const lic = store.licence
  const outside: { x1: number; x2: number }[] = []
  if (lic && lic.state !== 'granted') {
    if (lic.from !== null && lic.from > props.dayStart) outside.push({ x1: x(props.dayStart), x2: x(Math.min(lic.from, props.dayEnd)) })
    if (lic.to !== null && lic.to < props.dayEnd) outside.push({ x1: x(Math.max(lic.to, props.dayStart)), x2: x(props.dayEnd) })
  }
  return { path, area, observedPath, forecastPath, surgedPath, yTicks, xTicks, marks, bands, nowX, markX, seabedY, seabedHeight, passableY, ceilingY, ceilingHeight, limitY, vessel, min, max, cursorY, outside }
})

/** No curve at all, and the whole of the view lies outside what this device may read */
const wholeViewOutside = computed(() => {
  const lic = store.licence
  if (!lic || lic.state === 'granted') return false
  return (lic.from !== null && props.dayEnd <= lic.from) || (lic.to !== null && props.dayStart >= lic.to)
})
</script>

<template>
  <svg ref="svgEl" class="chart draggable" :class="{ settinglevel: levelCursor !== null && levelCursor !== undefined }" :viewBox="`0 0 ${W} ${H}`" role="img" :aria-label="store.t('tide')"
       @pointerdown="onPointerDown" @pointermove="onPointerMove" @pointerup="onPointerUp"
       @pointercancel="onPointerUp" @wheel="onWheel">
    <template v-if="view">
      <g v-for="t in view.yTicks" :key="t.m">
        <line :x1="pad.left" :x2="W - pad.right" :y1="t.y" :y2="t.y" stroke="var(--line)" stroke-width="1" />
        <text :x="pad.left - 6" :y="t.y + 4" text-anchor="end" font-size="10" fill="var(--fg-3)" class="num">{{ t.m.toFixed(1) }}</text>
      </g>
      <g v-for="t in view.xTicks" :key="t.x">
        <line :x1="t.x" :x2="t.x" :y1="pad.top" :y2="pad.top + ch" stroke="var(--line)" stroke-width="1" />
        <text :x="t.x" :y="H - 8" text-anchor="middle" font-size="10" fill="var(--fg-3)" class="num">{{ t.label }}</text>
      </g>
      <g v-if="view.cursorY !== null">
        <line :x1="pad.left" :x2="W - pad.right" :y1="view.cursorY" :y2="view.cursorY"
              stroke="var(--accent)" stroke-width="1.5" stroke-dasharray="6 4" />
        <rect :x="W - pad.right - 36" :y="view.cursorY - 10" width="36" height="20" rx="4" fill="var(--accent)" pointer-events="none" />
        <text :x="W - pad.right - 18" :y="view.cursorY + 4" text-anchor="middle" font-size="10" fill="#fff"
              class="num" pointer-events="none">{{ store.displayLevel(levelCursor ?? 0).toFixed(2) }}</text>
      </g>
      <g v-if="view.seabedY !== null">
        <rect :x="pad.left" :y="view.seabedY" :width="cw" :height="view.seabedHeight" fill="var(--seabed)" />
        <line :x1="pad.left" :x2="W - pad.right" :y1="view.seabedY" :y2="view.seabedY" stroke="var(--seabed-line)" stroke-width="1.5" />
      </g>
      <g v-for="(b, i) in view.bands" :key="'band' + i" class="band" :class="b.state">
        <rect :x="b.x1" :y="pad.top - 7" :width="Math.max(0, b.x2 - b.x1)" height="9" />
        <text v-if="b.text" :x="b.mid" :y="pad.top" text-anchor="middle" font-size="7" class="num">{{ b.text }}</text>
        <text v-if="b.above" :x="b.mid" :y="pad.top - 9" text-anchor="middle" font-size="7.5" class="num above">{{ b.above }}</text>
      </g>
      <g v-if="view.ceilingY !== null">
        <rect :x="pad.left" :y="pad.top" :width="cw" :height="view.ceilingHeight" fill="var(--bridge)" />
        <line :x1="pad.left" :x2="W - pad.right" :y1="view.ceilingY" :y2="view.ceilingY" stroke="var(--bridge-line)" stroke-width="1.5" />
      </g>
      <path :d="view.area" fill="var(--accent)" opacity="0.12" />
      <path :d="view.path" fill="none" stroke="var(--accent)" :stroke-width="view.observedPath || view.forecastPath || view.surgedPath ? 1.5 : 2" stroke-linejoin="round" />
      <!-- the old app drew the skipper's own rise or fall as a green line -->
      <path v-if="view.surgedPath" :d="view.surgedPath" fill="none" stroke="var(--ok)" stroke-width="2.25" stroke-linejoin="round" stroke-linecap="round" />
      <path v-if="view.forecastPath" :d="view.forecastPath" fill="none" stroke="var(--forecast)" stroke-width="2" stroke-dasharray="5 4" stroke-linejoin="round" stroke-linecap="round" />
      <path v-if="view.observedPath" :d="view.observedPath" fill="none" stroke="var(--observed)" stroke-width="2.25" stroke-linejoin="round" stroke-linecap="round" />
      <g v-for="(m, i) in view.marks" :key="i">
        <circle :cx="m.x" :cy="m.y" r="3.5" :fill="m.type === 'HW' ? 'var(--hw)' : 'var(--lw)'" />
        <text :x="m.lx" :y="m.ly" :text-anchor="m.anchor" font-size="10" :fill="m.type === 'HW' ? 'var(--hw)' : 'var(--lw)'" class="num">{{ m.label }}</text>
      </g>
      <line v-if="view.limitY !== null" :x1="pad.left" :x2="W - pad.right" :y1="view.limitY" :y2="view.limitY" stroke="var(--passable)" stroke-width="1.5" stroke-dasharray="6 3" />
      <line v-if="view.passableY !== null" :x1="pad.left" :x2="W - pad.right" :y1="view.passableY" :y2="view.passableY" stroke="var(--passable)" stroke-width="1.5" stroke-dasharray="6 3" />
      <g v-if="view.vessel" class="vessel">
        <!-- mast, or keel for a shallow -->
        <line v-if="view.vessel.mastY !== null" :x1="view.vessel.x" :x2="view.vessel.x" :y1="view.vessel.deckY" :y2="view.vessel.mastY" class="spar" />
        <path v-if="view.vessel.mastY !== null" :d="`M${view.vessel.x + 1.5} ${view.vessel.mastY + 3} L${view.vessel.x + 8} ${view.vessel.mastY + (view.vessel.deckY - view.vessel.mastY) * 0.6} L${view.vessel.x + 1.5} ${view.vessel.mastY + (view.vessel.deckY - view.vessel.mastY) * 0.6} Z`" class="sail" />
        <line v-if="view.vessel.keelY !== null" :x1="view.vessel.x" :x2="view.vessel.x" :y1="view.vessel.deckY" :y2="view.vessel.keelY" class="spar" />
        <!-- hull straddling the waterline -->
        <path :d="`M${view.vessel.x - 11} ${view.vessel.deckY - 3} L${view.vessel.x + 11} ${view.vessel.deckY - 3} L${view.vessel.x + 6} ${view.vessel.deckY + 4} L${view.vessel.x - 6} ${view.vessel.deckY + 4} Z`" class="hull" />
      </g>
      <g v-if="view.vessel && view.vessel.dim" class="dimension" :class="fits ? 'fits' : 'short'">
        <line :x1="view.vessel.dim.x" :x2="view.vessel.dim.x" :y1="view.vessel.dim.from" :y2="view.vessel.dim.to" />
        <line :x1="view.vessel.dim.x - 4" :x2="view.vessel.dim.x + 4" :y1="view.vessel.dim.from" :y2="view.vessel.dim.from" />
        <line :x1="view.vessel.dim.x - 4" :x2="view.vessel.dim.x + 4" :y1="view.vessel.dim.to" :y2="view.vessel.dim.to" />
        <text :x="view.vessel.dim.x + 6" :y="(view.vessel.dim.from + view.vessel.dim.to) / 2 + 3" font-size="10" class="num">{{ view.vessel.dim.label }}</text>
      </g>
      <line v-if="view.markX !== null && !view.vessel" class="nowline" :x1="view.markX" :x2="view.markX" :y1="pad.top - 6" :y2="pad.top + ch" stroke="var(--now)" stroke-width="1.5" stroke-dasharray="3 3" />
      <g v-for="(o, i) in view.outside" :key="'outside' + i" class="outside">
        <line :x1="o.x1" :x2="o.x2" :y1="pad.top + ch / 2" :y2="pad.top + ch / 2" stroke="var(--bad)" stroke-width="1.5" />
        <text v-if="o.x2 - o.x1 > 90" :x="(o.x1 + o.x2) / 2" :y="pad.top + ch / 2 - 6" text-anchor="middle" font-size="10" fill="var(--bad)">{{ store.t('outsideDemo') }}</text>
      </g>
    </template>
    <template v-else-if="wholeViewOutside">
      <line :x1="pad.left" :x2="W - pad.right" :y1="H / 2" :y2="H / 2" stroke="var(--bad)" stroke-width="1.5" />
      <text :x="W / 2" :y="H / 2 - 6" text-anchor="middle" font-size="10" fill="var(--bad)">{{ store.t('outsideDemo') }}</text>
    </template>
    <text v-else :x="W / 2" :y="H / 2" text-anchor="middle" font-size="12" fill="var(--fg-2)">{{ store.t('noData') }}</text>
  </svg>
</template>
